import { FC, useRef, useCallback, ChangeEvent, DragEvent } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import InputCheckbox from 'modules/_shared/components/InputCheckbox';
import IconTooltip from 'modules/_shared/components/Icon/IconTooltip';

import { useAcceptTradingOfferModal } from '../context/AcceptTradingOfferModalContext';

const ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png';

const AcceptTradingOfferModalStep2: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useAcceptTradingOfferModal();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const partnerAddress = data.partnerBuyer?.address;

  const handleFileSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files) {
        Array.from(files).forEach((file) => actions.handleAddDocument(file));
      }
      e.target.value = '';
    },
    [actions]
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const { files } = e.dataTransfer;
      if (files) {
        Array.from(files).forEach((file) => actions.handleAddDocument(file));
      }
    },
    [actions]
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div className="nk-stepper-step active">
      <div className="text-center mb-4">
        <em className="icon ni ni-shield-check fs-24px text-primary" />
        <h5 className="mt-2">{t('DataVerification')}</h5>
        <p className="text-soft">{t('ConfirmYourDataToCompleteOperation')}</p>
      </div>

      <div className="row">
        <div className="col-12 mb-2">
          <h6 className="overline-title text-soft">{t('PersonalData')}</h6>
        </div>
        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label className="form-label d-flex align-items-center">
              {t('FullName')}
              <IconTooltip
                id="accept-offer-fullname-tooltip"
                tooltip={t('FullNameAsInIdentityDocument')}
                place="right"
                className="fs-11px text-soft"
              />
            </label>
            <input
              type="text"
              className="form-control"
              value={data.partnerBuyer?.name || ''}
              onChange={(e) =>
                actions.handleUpdatePartnerBuyerField('name', e.target.value)
              }
            />
          </div>
        </div>
        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label className="form-label">{t('NifNie')}</label>
            <input
              type="text"
              className="form-control"
              value={data.partnerBuyer?.cif || ''}
              onChange={(e) =>
                actions.handleUpdatePartnerBuyerField('cif', e.target.value)
              }
            />
          </div>
        </div>
        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label className="form-label">{t('Email')}</label>
            <input
              type="email"
              className="form-control"
              value={data.partnerBuyer?.email || ''}
              onChange={(e) =>
                actions.handleUpdatePartnerBuyerField('email', e.target.value)
              }
            />
          </div>
        </div>
        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label className="form-label">{t('Telephone')}</label>
            <input
              type="tel"
              className="form-control"
              value={data.partnerBuyer?.phone || ''}
              onChange={(e) =>
                actions.handleUpdatePartnerBuyerField('phone', e.target.value)
              }
            />
          </div>
        </div>

        <div className="col-12 mb-2">
          <h6 className="overline-title text-soft">{t('FiscalAddress')}</h6>
        </div>
        <div className="col-12 mb-3">
          <div className="form-group">
            <label className="form-label">{t('FullAddress')}</label>
            <input
              type="text"
              className="form-control"
              value={partnerAddress?.line1 || ''}
              onChange={(e) =>
                actions.handleUpdatePartnerBuyerAddressField(
                  'line1',
                  e.target.value
                )
              }
            />
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="form-group">
            <label className="form-label">{t('PostalCode')}</label>
            <input
              type="text"
              className="form-control"
              value={partnerAddress?.zip || ''}
              onChange={(e) =>
                actions.handleUpdatePartnerBuyerAddressField(
                  'zip',
                  e.target.value
                )
              }
            />
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="form-group">
            <label className="form-label">{t('City')}</label>
            <input
              type="text"
              className="form-control"
              value={partnerAddress?.city || ''}
              onChange={(e) =>
                actions.handleUpdatePartnerBuyerAddressField(
                  'city',
                  e.target.value
                )
              }
            />
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="form-group">
            <label className="form-label">{t('Country')}</label>
            <input
              type="text"
              className="form-control"
              value={partnerAddress?.country || ''}
              onChange={(e) =>
                actions.handleUpdatePartnerBuyerAddressField(
                  'country',
                  e.target.value
                )
              }
            />
          </div>
        </div>

        <div className="col-12 mb-2">
          <h6 className="overline-title text-soft">{t('Documentation')}</h6>
        </div>
        <div className="col-12">
          <div className="form-group">
            <label className="form-label d-flex align-items-center">
              {t('AttachIdentityDocument')}
              <IconTooltip
                id="accept-offer-identity-doc-tooltip"
                tooltip={t('IdentityDocumentTooltip')}
                place="right"
                className="fs-24px text-soft"
              />
            </label>
            <div
              className="upload-zone border border-dashed rounded p-4 text-center dropzone dz-clickable"
              role="button"
              tabIndex={0}
              onClick={handleUploadClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleUploadClick();
                }
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <em className="icon ni ni-upload-cloud fs-24px text-soft" />
              <p className="text-soft mb-2">{t('DragDocumentHereOr')}</p>
              <button
                type="button"
                className="btn btn-dim btn-sm btn-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUploadClick();
                }}
              >
                {t('SelectFile')}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="d-none"
                accept={ACCEPTED_FILE_TYPES}
                onChange={handleFileSelect}
              />
              <p className="text-soft fs-11px mt-2 mb-0">
                {t('IdentityDocumentFormats')}
              </p>
            </div>
            {data.buyerDocuments.length > 0 && (
              <div className="mt-2">
                {data.buyerDocuments.map((file, index) => (
                  <div
                    key={`${file.name}-${file.size}-${file.lastModified}`}
                    className="d-flex align-items-center justify-content-between py-1 px-2 bg-light rounded mb-1"
                  >
                    <span className="text-soft fs-12px text-truncate flex-grow-1 mr-2">
                      {file.name}
                    </span>
                    <button
                      type="button"
                      className="btn btn-icon btn-sm btn-outline-danger"
                      onClick={() => actions.handleRemoveDocument(index)}
                      aria-label={t('Remove')}
                    >
                      <em className="icon ni ni-trash" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="col-12 mt-2">
          <InputCheckbox
            id="verify-data-checkbox"
            label={t('ConfirmDataIsCorrect')}
            checked={data.isDataVerified}
            onChange={actions.handleToggleDataVerified}
          />
        </div>
      </div>
    </div>
  );
};

export default AcceptTradingOfferModalStep2;
